import type { OrganizationsRow } from '@staffingengine/database-types'

interface OrganizationMetadata {
  onereach?: {
    account_id?: string
  }
  bullhorn?: {
    cluster_id?: string
    swim_lane_id?: string
  }
}

export type Products =
  | 'alerts'
  | 'analytics'
  | 'enginegpt'
  | 'lead_distribution'
  | 'chatbot'
  | 'meeting_booking'

export interface OrganizationProducts extends Record<Products | string, boolean> {}

export interface Organization extends OrganizationsRow {
  metadata: OrganizationMetadata | any | null
  products?: OrganizationProducts | null
}

export const useOrganizationStore = defineStore('organization', () => {
  const client = useDatabaseClient()

  const isLoading = ref(false)
  const organization = ref<Organization | undefined>(undefined)
  const onereachAccountId = computed(() => organization.value?.metadata?.onereach?.account_id)

  //
  // Products
  //

  const productsEnabled = computed(() => {
    if (!organization.value?.products) return new Set([])

    const filteredProducts = Object.entries(organization.value.products)
      .map(([name, value]) => (value ? name : undefined))
      .filter((product) => product !== undefined)

    return new Set(filteredProducts)
  })

  const hasProduct = (product: Products | string) => productsEnabled.value.has(product)

  //
  // Utils
  //

  const reset = () => {
    organization.value = undefined
    isLoading.value = false
  }

  // ---
  // Fetch
  // ---

  /**
   * Fetch with the currently set pagination and filtering
   *
   * @param options
   * @param options.reset - request from page 1
   */
  const fetchById = async (id: string) => {
    try {
      isLoading.value = true

      const response = await client.organizations.fetchById(id)

      if (response.data) {
        organization.value = response.data as Organization
      }

      isLoading.value = false
    } catch (err) {
      const error = err as Error
      isLoading.value = false

      throw error
    }
  }

  return {
    isLoading,
    organization,
    onereachAccountId,
    productsEnabled,
    hasProduct,
    fetchById,
    reset,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrganizationStore, import.meta.hot))
}
